import {computed, EventEmitter, inject, Injectable, signal} from "@angular/core";
import {HttpClient} from "@angular/common/http";
import {Title} from "@angular/platform-browser";
import * as utils from '../lib/utils';
import {MatDialog, MatDialogConfig, MatDialogRef} from "@angular/material/dialog";
import {DownloadFormDialogComponent} from "../forms/file-download-form/file_download_form.component";
import {EditHistoryComponent} from "../forms/edit-history.component";
import {AppScope} from './app_scope.service';
import {BehaviorSubject, Observable, ReplaySubject, Subject} from "rxjs";
import {DateTimePeriodService} from "./date-time-period.service";
import {takeUntil, tap} from "rxjs/operators";
import {ReportIssueFormDialogComponent} from '../forms/report-issue-form-dialog/report-issue-form-dialog.component';
import {BreadcrumbsService} from "../menu-bars/breadcrumbs/breadcrumbs.service";
import {MatSnackBarRef} from "@angular/material/snack-bar";
import {Process} from "../_models/process";
import {NotificationService} from "./notification.service";
import {IDateTimePeriod} from "../_typing/date-time-period";
import {ModelID} from "../_typing/generic-types";
import {MenuItem} from "../menu-bars/menu-item.model";
import {
    CalculationErrorNotificationComponent
} from "../notifications/calculation-error-notification/calculation-error-notification.component";
import {JobNotificationComponent} from "../notifications/job-notification/job-notification.component";
import {BackgroundJobService, getMessageStringFromJobPollResponse} from "./background-job.service";
import {DynamicNotificationComponent} from "../_typing/notification";
import Keycloak from 'keycloak-js';
import {SecurePipe} from "../shared/pipes";
import {ThemeService} from "./theme.service";
import {RIGHT_LOGO_URL} from "../shared/globals";

interface IButtons {
    name: string;
    func: (params?: any) => any;
    params: any;
    class: string;
    toggle: boolean;
}

// TODO: In a code cleanup, move these to a shared location
`add something like this
const MENU_ITEM_CONFIGS: Record<HeaderButton, MenuItemConfig> = {
    [HeaderButton.MANAGE_USERS]: {
        name: HeaderButton.MANAGE_USERS,
        path: '/view/user_list',
        icon: 'fa fa-users',
        isAngularRoute: true
    },
    [HeaderButton.REGISTER_USER]: {
        name: HeaderButton.REGISTER_USER,
        path: '/view/register_user',
        icon: 'fa fa-user',
        isAngularRoute: true
    },
    [HeaderButton.ACCESS_TOKEN]: {
        name: HeaderButton.ACCESS_TOKEN,
        path: '/view/access_token',
        icon: 'fa fa-user',
        isAngularRoute: true
    },
} as const; `

export enum PermissionCheckType {
    ALL = "all",
    ANY = "any"
}

interface FeatureCheck {
    features: FeatureName[];
    type?: PermissionCheckType;
    platformCheck?: boolean;
}

export enum FeatureName {
    MANAGE_USERS = "manage_users",
    LAUNCH_ADMIN_GUI = "launch_admin_gui",
    EDIT_USERS = "edit users",
    VIEW_MANAGE_USERS = "view manage users",
}

export enum HeaderButton {
    REPORT_ISSUE = "Report Issue",
    HELP = "Help",
    ADMIN_PORTAL = "Admin Portal",
    MANAGE_USERS = "Manage Users",
    REGISTER_USER = "Register User",
    ACCESS_TOKEN = "Access Token",
    MY_ACCOUNT = "My Account",
    MY_SECURITY = "My Security",
    LOGOUT = "Logout",
}

type ButtonsType = Partial<IButtons>;

@Injectable({
    providedIn: 'root'
})

export class HeaderDataService {
    buttons: ButtonsType[] = [];
    component_buttons_loaded: boolean = false;

    public _title: string = 'Wire';
    public titleSubject: Subject<string> = new BehaviorSubject('Wire');

    readonly printToPdfClicked: Subject<void> = new Subject<void>();
    toggleGridMode: Subject<boolean> = new Subject<boolean>();
    toggleLayoutMode: Subject<boolean> = new Subject<boolean>();
    page_edit_toggle: ReplaySubject<boolean> = new ReplaySubject(1);
    tile_edit_mode: BehaviorSubject<boolean> = new BehaviorSubject(false); // Tells the page if any tile is being edited (layout)
    page_print_toggle: ReplaySubject<boolean> = new ReplaySubject(1);
    hideWireLogo: ReplaySubject<boolean> = new ReplaySubject(1);
    page_edit_mode: boolean = false;
    public showingCommentPanel: ReplaySubject<{ toggle: boolean, comment_data?: any }> = new ReplaySubject<{
        toggle: false
    }>(1);
    //TODO Move these to AppScope
    readonly dtpReset: EventEmitter<IDateTimePeriod>;
    readonly dtpChanged: EventEmitter<IDateTimePeriod>;

    public refreshVDTNodeSubject: Subject<ModelID[]> = new Subject();

    add_new_page_form: boolean = false;
    add_print: boolean = false;
    add_comment: boolean = false;
    add_upload: boolean = false;
    add_edit: boolean = true;
    show_dtp: boolean = false; // whether a dtp is available on the page
    dtp_visible: boolean = false; // whether a dtp is hidden or not (mobile)
    // finalPath = () => /[^/]*$/.exec(this.appScope.breadcrumbs)[0];
    add_present: boolean = true;
    page_menu_options: any = {
        'insights': 'Insights',
        'settings': 'Settings',
        'my_views': 'My Views'
    }; // defaults

    showing_comments: boolean = false;
    private readonly onDestroy: Subject<void> = new Subject<void>();
    // Emitted by PageViewComponent when printing. Can be used by any components that need to manually adapt
    public pagePrinting: Subject<boolean> = new Subject();
    public pageCommentButtonClicked: BehaviorSubject<boolean> = new BehaviorSubject(false);
    public page_selected = false;
    public hidePageLoader: boolean = false;
    public right_logo: string;
    public refreshTileSubject = new Subject<string>();
    private readonly keycloak = inject(Keycloak);

    private readonly _rightLogoUrl = signal<string>(RIGHT_LOGO_URL);
    // readonly signal for consumers
    readonly rightLogoUrl = computed(() => this._rightLogoUrl());

    constructor(private http: HttpClient,
                private titleService: Title,
                public dialog: MatDialog,
                private appScope: AppScope,
                private dateTimePeriodService: DateTimePeriodService,
                private breadcrumbs: BreadcrumbsService,
                private securePipe: SecurePipe,
                private notification: NotificationService,
                private themeService: ThemeService,
                private backgroundJobService: BackgroundJobService) {
        this.dtpReset = new EventEmitter();
        this.dtpChanged = new EventEmitter();
        this.page_edit_toggle.pipe(takeUntil(this.onDestroy))
            .subscribe((newBool: boolean) => {
                this.page_edit_mode = newBool;
            });
        this.themeService.rightLogoUrl$
            .subscribe((logoUrl) => {
                this._rightLogoUrl.set(logoUrl || RIGHT_LOGO_URL);
            });
    }

    checkFeatures(check: FeatureCheck): boolean {
        const {features, type = PermissionCheckType.ALL, platformCheck = true} = check;

        const hasRequiredFeatures = type === PermissionCheckType.ALL
            ? features.every(feature => this.appScope.current_user.feature_names.includes(feature))
            : features.some(feature => this.appScope.current_user.feature_names.includes(feature));

        return hasRequiredFeatures && (!platformCheck || this.appScope.isNotMobile);
    }

    addDownload() {
        if (this.buttons.find(b => b.name === 'Download')) {
            return;
        }
        this.buttons.push(
            {
                name: 'Download',
                func: this.downloadData,
                params: '',
                class: ''
            }
        );
    }

    removeDownload(): void {
        if (!this.buttons) {
            return;
        }
        this.buttons = this.buttons.slice(this.buttons.indexOf(this.buttons.find((b: ButtonsType): boolean => b.name === 'Download')));
    }

    downloadData = (): void => {
        this.openDownloadDialog();
    }

    addSection = (id?) => {
        return {
            id: id ? id : null,
            title: null,
            'class': 'medium',
            // height: this.section_height_dict["medium"].value,
            // fixed_height: this.section_height_dict["medium"].fixed,
            cols: 4,
            rows: 4,
            x: null,
            y: null,
            tiles: []
        };
    }

    reset(): void {
        this.buttons = [];
        this.component_buttons_loaded = false;
        this.show_dtp = false;
        this.add_new_page_form = false;
        this.add_comment = false;
        this.add_print = false;
        this.removeDownload();
        this.add_upload = false;
        this.add_edit = false;
        this.breadcrumbs.resetBreadcrumbs();
    }

    setPath(process: Process) {
        this.breadcrumbs.setPath(process);
    }

    setFolder(session) {
        this.breadcrumbs.setFolder(session);
    }

    set title(title: string) {
        this.titleService.setTitle(title);
        this._title = title;
        setTimeout(() => {
            this.titleSubject.next(title);
        });
    }

    get title(): string {
        return this._title;
    }

    public folder_options() {
        if (this.appScope.config_name_map['page_grouping_options']) {
            this.page_menu_options = this.appScope.config_name_map['page_grouping_options'].value;
        }
        return this.page_menu_options;
    }

    buildRightMenu() {
        let menuItems = [];
        let createMenuItem = function (name, link, item_class?, angular_link?, func?) {
            return {
                'name': name,
                'link': link,
                'class': item_class,
                angular_page: angular_link,
                func: func
            };
        };
        if (this.appScope.isNotMobile) {
            menuItems.push(
                createMenuItem('Report Issue',
                    null,
                    'fa fa-unlink', false,
                    this.openReportIssueDialog));
        }
        menuItems.push(
            createMenuItem('Help',
                '/view/help-page',
                'fa fa-question-circle', true));

        const activeAccountId = this.appScope.active_account_id;

        const isAdminOnCurrentAccount = this.appScope.isUserAdminOnCurrentAccount(activeAccountId);

        let featureNames = this.appScope.current_user.feature_names;

        if (this.checkFeatures({
            features: [FeatureName.LAUNCH_ADMIN_GUI, FeatureName.MANAGE_USERS],
            type: PermissionCheckType.ANY,
            platformCheck: true
        })) {
            menuItems.push(createMenuItem(HeaderButton.ADMIN_PORTAL, '/admin', 'fa fa-database'));
        }

        if (isAdminOnCurrentAccount || this.checkFeatures({
            features: [FeatureName.EDIT_USERS, FeatureName.VIEW_MANAGE_USERS],
            type: PermissionCheckType.ANY,
            platformCheck: true
        })) {
            menuItems.push(createMenuItem(HeaderButton.MANAGE_USERS, '/view/user_list', 'fa fa-users', true));
            menuItems.push(createMenuItem(HeaderButton.REGISTER_USER, '/view/register_user', 'fa fa-user', true));
            menuItems.push(createMenuItem(HeaderButton.ACCESS_TOKEN, '/view/access_token', 'fa fa-user', true));
        }

        menuItems.push(new MenuItem({
            name: HeaderButton.MY_ACCOUNT,
            link: this.keycloak.createAccountUrl({redirectUri: window.location.href}),
            style_class: 'fa fa-users'
        }));
        menuItems.push(new MenuItem({
            name: HeaderButton.MY_SECURITY,
            link: ['/view', 'my_security'],
            style_class: 'fa fa-users',
            angular_page: true
        }));

        menuItems.push(new MenuItem({
            name: HeaderButton.LOGOUT, func: () => {
                this.notification.openInfo("Logging out...");
                this.appScope.logout();
            }, style_class: 'fa fa-sign-out'
        }));

        return {name: this.appScope.current_user.name, items: menuItems};
    }

    isImage(src: string): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            this.securePipe.transform(src).subscribe({
                next: (safeUrl) => {
                    const image: HTMLImageElement = new Image();
                    image.onerror = () => resolve(false);
                    image.onload = () => resolve(true);
                    image.src = safeUrl.toString();
                },
                error: () => {
                    // If the HTTP request fails, the image doesn't exist
                    resolve(false);
                },
                complete: () => {
                    // Optional: handle completion if needed
                }
            });
        });
    }

    openDownloadDialog = (data?): void => {
        const config: MatDialogConfig = {};
        if (data) {
            config.data = data;
        }
        config.panelClass = ['default-form-dialog', 'download-form-dialog'];
        const dialogRef = this.dialog.open(DownloadFormDialogComponent, config);
    }

    openReportIssueDialog = () => {
        const config: MatDialogConfig = {};
        config.data = {"page_title": this.title};
        config.panelClass = 'report-issue-form-dialog';
        const dialogRef = this.dialog.open(ReportIssueFormDialogComponent, config);
    }

    queueAggregations(dtp: IDateTimePeriod, seriesList: string[]): Observable<any> {
        if (!dtp.start || !dtp?.end) {
            this.notification.openError('Please refresh your Time Traveler then try again.');
        }

        const params = {
            start: dtp.start.toISOString(),
            end: dtp.end.toISOString(),
            series_string: seriesList.join(','),
            override: true,
        };

        const url = '/api/utils/BulkImportHistorianStatusData?' + utils.httpParamSerializer(params);

        return this.http.get(url).pipe(
            tap({
                next: (data: any) => {
                    this.notification.openSuccess('Aggregations updated successfully.');
                },
                error: (error: any) => {
                    const errorMessage = error?.error?.message || 'Failed to update aggregations.';
                    this.notification.openError(errorMessage, 10000);
                },
            }),
        );
    }

    getCalculations(dtp, series_list, sample_period, extra_hours) {
        if (!dtp?.start && dtp?.end) {
            this.notification.openError("Please refresh your Time Traveler then try again.");
        }
        let p = new Promise((resolve, reject) => {
            if (confirm('Selecting to update calculations will delete and replace all existing calculations in the selected range.')) {
                let extra_end = utils.deepCopy(dtp.end)['addHours'](extra_hours);

                let params = {
                    series_list: series_list,
                    deepness: 4,
                    insert_from_time: dtp.start.toISOString(),
                    start: dtp.start.toISOString(),
                    end: extra_end.toISOString(),
                    insert_data: true,
                    deep: true,
                    sample_period: sample_period,
                    run_dependent_calcs: true,
                    override: true
                };

                this.http.get("/api/GetCalcs" + '?' + utils.httpParamSerializer(params)).pipe(tap({
                    next: data => {
                        const jobId = data['job'];
                        this.showJobSuccess(jobId, "Calculation");
                        this.sendEvent(true, series_list);
                        resolve(data);
                    }, error: error => {
                        if (error === 'Not confirmed.') {
                            this.notification.openError('Failed to update calculations ' + error);
                        } else if (error.status === 423) {
                            this.notification.openError(error.error.message, 10000);
                        } else if (this.checkCalculationError(error, "Error updating calculations.")) {
                            //Handled in checkCalculationError
                        } else {
                            this.notification.openError(
                                error?.error?.error?.message || error?.error?.message
                                || 'Failed to update calculations.', 10000);
                        }
                        this.sendEvent(false, series_list);
                        reject(error);
                    }
                })).subscribe();

            } else {
                reject("Not confirmed.");
            }
        });
        return p;
    }

    showJobSuccess(jobId: ModelID, jobType: string): void {
        if (!jobType) {
            jobType = "Task";
        }
        let notificationRef: MatSnackBarRef<DynamicNotificationComponent> | null =
            this.notification.openDynamicSnackbar(JobNotificationComponent, {
                jobId: jobId,
                jobType: jobType,
                styleType: 'success'
            });

        this.backgroundJobService.createJobPoll(String(jobId)).pipe(
            takeUntil(this.onDestroy)
        ).subscribe({
            next: (jobResponse) => {
                if (jobResponse.processing) return;

                notificationRef?.dismiss();
                notificationRef = null;

                if (jobResponse.status) {
                    this.notification.openSuccess(`${jobType} completed successfully.`);
                } else {
                    const message = getMessageStringFromJobPollResponse(jobResponse);
                    if (message) {
                        this.notification.openError(message);
                    } else {
                        this.notification.openDynamicSnackbar(JobNotificationComponent, {
                            jobId: jobId,
                            jobType: `${jobType} failed`,
                            styleType: 'error'
                        });
                    }
                }
            },
            error: () => {
                notificationRef?.dismiss();
                notificationRef = null;
                this.notification.openError(`Failed to track ${jobType} status.`);
            }
        });
    }

    checkCalculationError(response: {
        status: number,
        error: { error: string }
    }, msg: string = "Calculation formula error found."): boolean {
        // A 4xx body carries either a formula string under error.error or a
        // structured {error: {message}}. Only the string form is a calc error.
        const errorDetails = typeof response.error?.error === 'string' ? response.error.error : null;
        const regex = /Calculation with name (.+?)'s formula/;
        const match = errorDetails?.match(regex);
        if (response.status === 422 && match?.[1]) {
            this.notification.openDynamicSnackbar(CalculationErrorNotificationComponent, {
                errorMessage: msg,
                errorDetails: errorDetails,
                seriesName: match[1],
                styleType: 'error'
            });

            //Return boolean doesn't need to wait for the subscription, just needs to report whether a calc error was found
            return true;
        }
        return false;
    }

    sendEvent(success: boolean, series_list) {
        (<any>window).dataLayer.push({
            event: 'updatecalculations',
            seriesList: series_list,
            success: success,
            click: 'updated calcs ' + success
        });
        // (<any>window).dataLayer.push({'click': 'updated calcs ' + success});

    }

    // correctionFactor(ev, dtp, series_list)
    correctionFactor(series?): MatDialogRef<EditHistoryComponent> {
        // TODO implement when MatDialog is added
        return this.dialog.open(EditHistoryComponent, {data: {series: series}});
    }

    toggleCommentPanel(toggle, comment_data) {
        this.showing_comments = toggle;
        this.showingCommentPanel.next({toggle: toggle, comment_data: comment_data});
    }

// TODO add constructor to session-state.ts (SessionState type)
    emptyPageJson() {
        return {
            id: null,
            relationships: {
                folders: {data: []},
                default_folder: {data: {}},
                user: {
                    data: {
                        id: this.appScope.current_user.id,
                        type: 'users'
                    }
                }
            },
            attributes: {
                json: {
                    // extracted: true, //Just for safety, can be removed once python migration is run
                    auto_refresh: false,
                    sample_period: 'hour',
                    calendar: this.dateTimePeriodService.defaultCalendar,
                    layout: "tile",
                    print_orientation: "portrait-widescreen",
                    range: 'yesterday',
                    refresh_time: 60,
                    sections: []
                },
                range: 'yesterday',
                report: 'Dashboard',
                download_url: '',
                name: null
            },
            type: 'session_state'
        };
    }
}
